import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorId: string | null;
}

// A safe, opaque id we can show the user / correlate with console logs. It
// contains NO stack trace, message, or secret.
function makeErrorId(): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `err-${Date.now().toString(36)}-${rand}`;
}

// ErrorBoundary keeps the lobby visible if a render throws (e.g. an unexpected
// shape from showcase-control). It never leaves only the blue background.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, errorId: null };

  static getDerivedStateFromError(): State {
    return { hasError: true, errorId: makeErrorId() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Developer-only diagnostics in the console; never surfaced to the UI.
    console.error('[demo-lobby] render error', this.state.errorId, error, info.componentStack);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, errorId: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          errorId={this.state.errorId ?? 'unknown'}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }
    return this.props.children;
  }
}

type Health = 'checking' | 'up' | 'down';

function ErrorFallback({
  errorId,
  onRetry,
  onReload,
}: {
  errorId: string;
  onRetry: () => void;
  onReload: () => void;
}): JSX.Element {
  const [health, setHealth] = useState<Health>('checking');

  useEffect(() => {
    let active = true;
    // /api/config is the open, non-secret showcase-control reachability probe
    // (proxied same-origin). It tells the presenter whether the backend is up.
    fetch('/api/config', { headers: { Accept: 'application/json' } })
      .then((r) => { if (active) setHealth(r.ok ? 'up' : 'down'); })
      .catch(() => { if (active) setHealth('down'); });
    return () => { active = false; };
  }, []);

  const healthLabel =
    health === 'checking' ? 'checking…' : health === 'up' ? 'reachable' : 'unreachable';

  return (
    <div className="app">
      <div className="demo-badge" role="status" aria-label="Demo mode">VIRTUAL CREDITS</div>
      <main className="layout">
        <section className="panel glass" role="alert">
          <h1>The demo lobby encountered an error</h1>
          <p className="muted">
            Something went wrong while rendering the lobby. Your demo data is safe — no real money
            is involved. You can retry rendering or reload the page.
          </p>
          <div className="create-row">
            <button className="btn btn-primary" onClick={onRetry}>Retry</button>
            <button className="btn btn-ghost" onClick={onReload}>Reload</button>
          </div>
          <div className="balance-row" style={{ marginTop: '1rem' }}>
            <span className="muted">showcase-control</span>
            <span className="service">
              <span className={`dot ${health === 'up' ? 'dot-up' : 'dot-down'}`} />
              <span className="muted">{healthLabel}</span>
            </span>
          </div>
          <p className="muted" style={{ marginTop: '0.75rem' }}>Error reference: <code>{errorId}</code></p>
        </section>
      </main>
      <footer className="footer">
        DEMO / VIRTUAL CREDITS ONLY — simulation and development environment. Not independently certified.
      </footer>
    </div>
  );
}
